import { useEffect, useMemo } from 'react'
import { Location } from 'react-router-dom'
import { Dispatch } from 'redux'
import { debounce } from 'debounce'
import isEqual from 'lodash/isEqual'
import { IPublishedSearch } from '../types/publishedSearch'
import { IQuery } from '../types/search'
import * as publishedSearchActions from '../actions/publishedSearch'
import * as searchActions from '../actions/search'
import { push } from '../actions/router'
import * as services from '../services/search'
import { isEmptyObject } from '../services/common'

export interface IUseSearchParams {
  search: IPublishedSearch
  location: Location<any>
  groupId: string
  isConsumer: boolean
  dispatch: Dispatch
}

export const useSearch = ({
  search: { items },
  location,
  groupId,
  isConsumer,
  dispatch,
}: IUseSearchParams): [() => void, (updatedQuery: IQuery) => void] => {
  const actions = useMemo(
    () => ({
      searchPacks: isConsumer
        ? publishedSearchActions.searchPublishedPacks
        : searchActions.searchPacks,
      updateSkipQuery: isConsumer
        ? publishedSearchActions.updatePublishedSkipQuery
        : searchActions.updateSkipQuery,
      resetSearch: isConsumer
        ? publishedSearchActions.resetPublishedSearch
        : searchActions.resetSearch,
    }),
    [isConsumer]
  )
  const parsedQueryData = useMemo(
    () => services.parseNestedQuery(location.search),
    [location.search]
  )

  const debouncedDispatch = useMemo(() => debounce(dispatch, 300), [dispatch])

  useEffect(() => {
    // If it's main page (parsedQueryData is empty) dispatch without delay when searching groups
    isEmptyObject(parsedQueryData)
      ? dispatch(actions.searchPacks({ ...parsedQueryData, group: groupId }))
      : debouncedDispatch(
          actions.searchPacks({ ...parsedQueryData, group: groupId })
        )
  }, [debouncedDispatch, dispatch, actions, groupId, parsedQueryData])

  const takeNextChunk = () => {
    dispatch(actions.updateSkipQuery(items.length))
    dispatch(actions.searchPacks({ ...parsedQueryData, group: groupId }))
  }

  const updateFilters = (updatedQuery: IQuery) => {
    const filteredQuery = services.removeEmptyFilters(updatedQuery)
    if (!isEqual(filteredQuery, parsedQueryData)) {
      dispatch(actions.resetSearch())
      dispatch(
        push(location.pathname, services.stringifyNestedQuery(filteredQuery))
      )
    }
  }

  useEffect(() => {
    dispatch(actions.resetSearch())
  }, [dispatch, actions, groupId])

  return [takeNextChunk, updateFilters]
}
